// ═══════════════════════════════════════════════════════════════
// BENEFIT CATALOG PICKER
// Path: src/components/shows/BenefitCatalogPicker.jsx
//
// Two-panel picker used inside PackageFormModal.
//   Left: Active catalog templates with checkboxes + search/category filter
//   Right: Selected benefits with drag handles + X to remove
//          + "Add custom benefit" free-text input at bottom
//
// Props:
//   benefits         — current benefits array [{ name, sort_order, source }]
//   onChange          — (newBenefits) => void
//   catalogTemplates — active benefit_templates rows from DB
//
// Blueprint: CMM_Show_Sponsorship_Blueprint_v1_1.md — Phase 4 (v1.1)
// ═══════════════════════════════════════════════════════════════

import React, { useState, useMemo } from "react";
import {
  Search,
  Filter,
  GripVertical,
  X,
  Plus,
  BookOpen,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";

export const BenefitCatalogPicker = ({
  benefits = [],
  onChange,
  catalogTemplates = [],
}) => {
  const { palette } = useApp();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [customInput, setCustomInput] = useState("");

  // Selected benefit names for quick lookup
  const selectedNames = useMemo(
    () => new Set(benefits.map((b) => b.name)),
    [benefits]
  );

  // Categories from catalog
  const categories = useMemo(() => {
    const cats = new Set();
    catalogTemplates.forEach((t) => {
      if (t.category) cats.add(t.category);
    });
    return Array.from(cats).sort();
  }, [catalogTemplates]);

  // Filtered catalog
  const filteredCatalog = useMemo(() => {
    let result = catalogTemplates;

    if (filterCategory) {
      result = result.filter((t) => t.category === filterCategory);
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          t.name.toLowerCase().includes(query) ||
          (t.category || "").toLowerCase().includes(query)
      );
    }
    return result;
  }, [catalogTemplates, filterCategory, searchQuery]);

  // ── Handlers ────────────────────────────────────────────────
  const toggleCatalogBenefit = (template) => {
    if (selectedNames.has(template.name)) {
      // Remove
      const updated = benefits.filter((b) => b.name !== template.name);
      onChange(reindex(updated));
    } else {
      // Add from catalog
      const newBenefit = {
        name: template.name,
        sort_order: benefits.length,
        source: "catalog",
      };
      onChange([...benefits, newBenefit]);
    }
  };

  const removeBenefit = (index) => {
    const updated = benefits.filter((_, idx) => idx !== index);
    onChange(reindex(updated));
  };

  const addCustomBenefit = () => {
    const name = customInput.trim();
    if (!name || selectedNames.has(name)) return;

    const newBenefit = {
      name,
      sort_order: benefits.length,
      source: "custom",
    };
    onChange([...benefits, newBenefit]);
    setCustomInput("");
  };

  const handleDrop = (dragIndex, dropIndex) => {
    if (dragIndex === dropIndex) return;
    const reordered = [...benefits];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    onChange(reindex(reordered));
  };

  // ── Render ──────────────────────────────────────────────────
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: 10,
        overflow: "hidden",
        minHeight: 300,
      }}
    >
      {/* LEFT PANEL: Catalog */}
      <div
        style={{
          flex: 1,
          borderRight: `1px solid ${palette.cardBorder}`,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "10px 12px",
            borderBottom: `1px solid ${palette.cardBorder}`,
            background: `${palette.accent1}08`,
          }}
        >
          <div
            style={{
              fontSize: FONT_SIZES.xs,
              fontWeight: 700,
              color: palette.text2,
              fontFamily: FONT_FAMILY,
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <BookOpen size={ICON_SIZES.sm} />
            Benefit Catalog
          </div>

          {/* Search */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: palette.inputBg,
              border: `1px solid ${palette.inputBorder}`,
              borderRadius: 6,
              padding: "4px 8px",
              marginBottom: 6,
            }}
          >
            <Search size={ICON_SIZES.xs} style={{ color: palette.text3 }} />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              style={{
                border: "none",
                outline: "none",
                background: "transparent",
                color: palette.inputColor,
                fontSize: FONT_SIZES.xs,
                fontFamily: FONT_FAMILY,
                width: "100%",
              }}
            />
          </div>

          {/* Category filter */}
          {categories.length > 0 && (
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              style={{
                width: "100%",
                padding: "4px 8px",
                borderRadius: 6,
                fontSize: FONT_SIZES.xs,
                fontFamily: FONT_FAMILY,
                background: palette.inputBg,
                color: palette.inputColor,
                border: `1px solid ${palette.inputBorder}`,
                cursor: "pointer",
              }}
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Catalog list */}
        <div style={{ flex: 1, overflowY: "auto", padding: 6 }}>
          {filteredCatalog.length === 0 ? (
            <p
              style={{
                textAlign: "center",
                color: palette.text3,
                fontSize: FONT_SIZES.xs,
                fontFamily: FONT_FAMILY,
                padding: 16,
              }}
            >
              {catalogTemplates.length === 0
                ? "No benefit templates yet."
                : "No matches."}
            </p>
          ) : (
            filteredCatalog.map((template) => {
              const isSelected = selectedNames.has(template.name);
              return (
                <label
                  key={template.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 8px",
                    borderRadius: 6,
                    cursor: "pointer",
                    background: isSelected ? `${palette.accent1}12` : "transparent",
                    transition: "background 0.15s",
                    marginBottom: 2,
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) e.currentTarget.style.background = `${palette.accent1}08`;
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) e.currentTarget.style.background = "transparent";
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleCatalogBenefit(template)}
                    style={{ cursor: "pointer", flexShrink: 0 }}
                  />
                  <span
                    style={{
                      fontSize: FONT_SIZES.xs,
                      color: palette.text1,
                      fontFamily: FONT_FAMILY,
                      flex: 1,
                    }}
                  >
                    {template.name}
                  </span>
                  {template.category && (
                    <span
                      style={{
                        fontSize: "0.6rem",
                        color: palette.accent1,
                        background: `${palette.accent1}15`,
                        padding: "1px 6px",
                        borderRadius: 8,
                        fontFamily: FONT_FAMILY,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      {template.category}
                    </span>
                  )}
                </label>
              );
            })
          )}
        </div>
      </div>

      {/* RIGHT PANEL: Selected Benefits */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <div
          style={{
            padding: "10px 12px",
            borderBottom: `1px solid ${palette.cardBorder}`,
            background: `${palette.accent1}08`,
          }}
        >
          <div
            style={{
              fontSize: FONT_SIZES.xs,
              fontWeight: 700,
              color: palette.text2,
              fontFamily: FONT_FAMILY,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            Package Benefits ({benefits.length})
          </div>
        </div>

        {/* Selected list */}
        <div style={{ flex: 1, overflowY: "auto", padding: 6 }}>
          {benefits.length === 0 ? (
            <p
              style={{
                textAlign: "center",
                color: palette.text3,
                fontSize: FONT_SIZES.xs,
                fontFamily: FONT_FAMILY,
                padding: 20,
              }}
            >
              Select benefits from the catalog or add custom ones below.
            </p>
          ) : (
            benefits.map((benefit, index) => (
              <div
                key={`${benefit.name}-${index}`}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData("text/plain", index.toString());
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const dragIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);
                  handleDrop(dragIndex, index);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 8px",
                  borderRadius: 6,
                  border: `1px solid ${palette.cardBorder}`,
                  background: palette.cardBg,
                  marginBottom: 4,
                  cursor: "grab",
                }}
              >
                <GripVertical
                  size={ICON_SIZES.sm}
                  style={{ color: palette.text3, flexShrink: 0 }}
                />
                <span
                  style={{
                    fontSize: FONT_SIZES.xs,
                    color: palette.text1,
                    fontFamily: FONT_FAMILY,
                    flex: 1,
                  }}
                >
                  {benefit.name}
                </span>
                <span
                  style={{
                    fontSize: "0.55rem",
                    fontWeight: 600,
                    color: benefit.source === "catalog" ? palette.accent1 : "#f59e0b",
                    background:
                      benefit.source === "catalog"
                        ? `${palette.accent1}15`
                        : "#fef3c7",
                    padding: "1px 5px",
                    borderRadius: 6,
                    fontFamily: FONT_FAMILY,
                    flexShrink: 0,
                  }}
                >
                  {benefit.source}
                </span>
                <button
                  onClick={() => removeBenefit(index)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: palette.text3,
                    padding: 2,
                    flexShrink: 0,
                    display: "flex",
                  }}
                >
                  <X size={ICON_SIZES.sm} />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Add custom benefit */}
        <div
          style={{
            padding: "8px 10px",
            borderTop: `1px solid ${palette.cardBorder}`,
            display: "flex",
            gap: 6,
          }}
        >
          <input
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addCustomBenefit();
              }
            }}
            placeholder="Add custom benefit..."
            style={{
              flex: 1,
              padding: "5px 8px",
              borderRadius: 6,
              fontSize: FONT_SIZES.xs,
              fontFamily: FONT_FAMILY,
              outline: "none",
              background: palette.inputBg,
              color: palette.inputColor,
              border: `1px solid ${palette.inputBorder}`,
            }}
          />
          <button
            onClick={addCustomBenefit}
            disabled={!customInput.trim() || selectedNames.has(customInput.trim())}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              padding: "5px 10px",
              borderRadius: 6,
              border: "none",
              background: palette.accent1,
              color: "#fff",
              fontSize: FONT_SIZES.xs,
              fontWeight: 600,
              fontFamily: FONT_FAMILY,
              cursor: customInput.trim() ? "pointer" : "not-allowed",
              opacity: customInput.trim() ? 1 : 0.5,
              flexShrink: 0,
            }}
          >
            <Plus size={ICON_SIZES.xs} />
            Add
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Helper: reindex sort_order ────────────────────────────────
function reindex(benefits) {
  return benefits.map((b, idx) => ({ ...b, sort_order: idx }));
}
